import React from "react";
import { useParams } from "react-router-dom";

import Navbar from "../../../components/en/Navbar";
import Footer from "../../../components/en/Footer";
import CtaNomal from "../../../components/en/CtaNomal";

import "../../../styles/common.css";
import "../../../styles/work.css";

import { works, resolveWork } from "../../../data/works";
import { tagLabels } from "../../../data/tagLabels";
import { relatedScore } from "../../../utils/relatedWorks";

const lang = "en";

const hasList = (arr) => Array.isArray(arr) && arr.length > 0;
const hasText = (v) => (typeof v === "string" ? v.trim().length > 0 : !!v);

const buildDisplayTags = (w) => {
  const industry = (w.industryTags ?? []).slice(0, 2);
  const companyCountry = w.company?.country ? [`companyCountry:${w.company.country}`] : [];
  return [...industry, ...companyCountry];
};

export default function WorkDetailTemplateEn() {
  const { slug } = useParams();

  const resolvedWorks = works.map(resolveWork);
  const work = resolvedWorks.find((w) => w.slug === slug);

  if (!work) return null;

  // --- related works ---
  const relatedWorks = resolvedWorks
    .filter((w) => w.slug !== slug)
    .map((w) => ({ w, score: relatedScore(w, work), sameCategory: w.category === work.category }))
    .sort((a, b) => {
      if (a.sameCategory !== b.sameCategory) return a.sameCategory ? -1 : 1;
      if (b.score !== a.score) return b.score - a.score;
      return (a.w.title?.[lang] ?? "").localeCompare(b.w.title?.[lang] ?? "");
    })
    .filter(({ score }) => score > 0)
    .slice(0, 12);

  // --- table fields (skip empty rows) ---
  const clientName = work.client?.name?.[lang] ?? "";
  const industryList = (work.industryTags ?? []).map((tag) => tagLabels[tag]?.[lang] ?? tag);
  const companyLocation = tagLabels[`companyCountry:${work.company?.country}`]?.[lang] ?? "";
  const overview = work.overview?.[lang] ?? "";
  const projectDetails = (work.serviceTags ?? []).map((tag) => tagLabels[tag]?.[lang] ?? tag);

  const serviceAreaList = [
    ...(work.target?.regions ?? []).map((r) => tagLabels[`targetRegion:${r}`]?.[lang] ?? r),
    ...(work.target?.countries ?? []).map((c) => tagLabels[`targetCountry:${c}`]?.[lang] ?? c),
  ];

  const objectiveList = (work.purposeTags ?? []).map((tag) => tagLabels[tag]?.[lang] ?? tag);
  const background = work.requestBackground?.[lang] ?? "";
  const release = work.release?.[lang] ?? "";
  const website = work.website ?? "";

  const rows = [
    { label: "Client Name", value: clientName, type: "text" },
    { label: "Industry", value: industryList, type: "list", className: "industry" },
    { label: "Company Location", value: companyLocation, type: "text" },
    { label: "Company Overview", value: overview, type: "text" },
    { label: "Project Details", value: projectDetails, type: "list", className: "jobPart" },
    { label: "Service Area", value: serviceAreaList, type: "list" },
    { label: "Objective", value: objectiveList, type: "list", className: "purpose" },
    { label: "Project Background", value: background, type: "text" },
    { label: "Release", value: release, type: "text" },
    { label: "Corporate Website", value: website, type: "link" },
  ].filter((row) => {
    if (row.type === "list") return hasList(row.value);
    return hasText(row.value);
  });

  const hasPc = !!work.webImages?.pc;
  const hasSp = !!work.webImages?.sp;
  const isOnlySp = hasSp && !hasPc;


  return (
    <>
      <Navbar />
      <main>
        {/* FV */}
        <section className="fvUnder">
          <div className="fvUnder-text">
            <h1>{work.title?.[lang]}</h1>
            <nav aria-label="Breadcrumb" className="breadcrumb">
              <ol className="breadcrumb">
                <li><a href="/en">Home</a></li>
                <li><a href="/en/work">Portfolio</a></li>
                <li aria-current="page">{work.title?.[lang]}</li>
              </ol>
            </nav>
          </div>
        </section>

        {/* Detail */}
        <section className="workDetail">
          <div
            className={`workDetail-main ${work.mainVideo?.isPortrait ? "workDetail-main--portrait" : ""
              }`}
          >
            {work.mainVideo?.src ? (
              <video controls playsInline poster={work.mainVideo.poster}>
                <source src={work.mainVideo.src} type="video/mp4" />
                Your browser does not support video playback.
              </video>
            ) : (
              <img src={work.thumbnail} alt={work.title?.[lang] ?? ""} />
            )}
          </div>

          <div className="workDetail-text">
            {Array.isArray(work.ctas) && work.ctas.length > 0 && (
              <div className="workDetail-cta">
                {work.ctas.map((cta, index) => (
                  <div
                    key={`${cta.url}-${index}`}
                    className="button-content button-arrow button-black"
                  >
                    <a href={cta.url} target="_blank" rel="noopener noreferrer">
                      {cta.label?.[lang] ?? cta.label}
                    </a>
                  </div>
                ))}
              </div>
            )}

            <table className="workDetail-text__table">
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label}>
                    <th>{row.label}</th>
                    <td>
                      {row.type === "list" ? (
                        <ul className={row.className}>
                          {row.value.map((v) => (
                            <li key={v}>{v}</li>
                          ))}
                        </ul>
                      ) : row.type === "link" ? (
                        <a href={row.value} target="_blank" rel="noopener noreferrer" class="button-underLine">
                          {row.value}
                        </a>
                      ) : (
                        row.value
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* {Array.isArray(work.images) && work.images.length > 0 && (
            <>
              {work.images.map((src, i) => (
                <div className="workDetail-mock" key={`${work.slug}-img-${i}`}>
                  <img src={src} alt={`${work.title?.[lang] ?? ""} ${i + 1}`} />
                </div>
              ))}
            </>
          )} */}
          {Array.isArray(work.images) && work.images.length > 0 ? (
            <>
              {work.images.map((src, i) => (
                <div className="workDetail-mock" key={`${work.slug}-img-${i}`}>
                  <img src={src} alt={`${work.title?.[lang] ?? ""} ${i + 1}`} />
                </div>
              ))}
            </>
          ) : Array.isArray(work.videos) && work.videos.length > 0 ? (
            <>
              {work.videos.map((video, i) => (
                <div
                  className={`workDetail-mock ${video.isPortrait ? "portrait" : ""}`}
                  key={`${work.slug}-video-${i}`}
                >
                  <video controls playsInline poster={work.mainVideo.poster}>
                    <source src={video.src} type="video/mp4" />
                    Your browser does not support video playback.
                  </video>
                </div>
              ))}
            </>
          ) : null}

          {work.webImages && (hasPc || hasSp) && (
            <div className={`workDetail-webImg ${isOnlySp ? "only-sp" : ""}`}>
              {hasPc && (
                <div className="workDetail-webImg__pc">
                  <img
                    src={work.webImages.pc}
                    alt={`${work.title?.[lang] ?? ""} PC`}
                  />
                </div>
              )}
              {hasSp && (
                <div className="workDetail-webImg__sp">
                  <img
                    src={work.webImages.sp}
                    alt={`${work.title?.[lang] ?? ""} SP`}
                  />
                </div>
              )}
            </div>
          )}
        </section>

        {/* Related */}
        <section className="otherWork">
          <div className="title">
            <p className="title-lead">OTHER WORKS</p>
            <h2>Related Work</h2>
          </div>

          <div className="work-box">
            {relatedWorks.map(({ w }) => {
              const displayTags = buildDisplayTags(w);
              return (
                <article key={w.slug}>
                  <a href={`/en/work/${w.slug}`}>
                    <div className="work-box__image">
                      <img src={w.thumbnail} alt={w.title?.[lang] ?? ""} />
                    </div>
                    <div className="work-box__text">
                      <h3>{w.title?.[lang]}</h3>
                      {displayTags.length > 0 && (
                        <ul className="work-box__tag">
                          {displayTags.map((tag) => (
                            <li key={tag}>{tagLabels[tag]?.[lang] ?? tag}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </a>
                </article>
              );
            })}
          </div>

          <div className="button-content button-arrow button-black">
            <a href="/en/work">View All</a>
          </div>
        </section>

        <CtaNomal />
      </main>
      <Footer />
    </>
  );
}
